/**
 * `POST /internal/cron/revenue-funding-materialize`.
 *
 * Backfill (control-loops ticks it hourly; a tick with nothing unmarked is two small SELECTs) of sealed UTC+8 usage days into
 * `revenue_funding_daily_rollup` / `_days`. Derived tables only.
 * Request-path `/summary` reads an unmarked day live and does not write.
 *
 * cron_run_history is written by control-loops' tick-level recordCronRun.
 */

export const REVENUE_FUNDING_MATERIALIZE_LOOKBACK_DAYS_DEFAULT = 30;
export const REVENUE_FUNDING_MATERIALIZE_LOOKBACK_DAYS_MAX = 120;
export const REVENUE_FUNDING_MATERIALIZE_MAX_DAYS_PER_CALL = 4;
export const REVENUE_FUNDING_MATERIALIZE_STATEMENT_TIMEOUT_MS = 8_000;
/** Route wall-clock budget. The control-loops fetch/handler timeouts sit above this. */
export const REVENUE_FUNDING_MATERIALIZE_WALL_MS = 25_000;

/**
 * Operator bounds (job-controls knobs of `revenue_funding_materialize`). `max_days` may go up (more days
 * a call); the statement and wall budgets may only be lowered: no preset or knob raises them above the
 * compiled values, so a day that does not fit 8 s stays live-computed instead of being given more time.
 */
export const REVENUE_FUNDING_MATERIALIZE_MAX_DAYS_CEILING = 16;
export const REVENUE_FUNDING_MATERIALIZE_STATEMENT_TIMEOUT_MIN_MS = 2_000;
export const REVENUE_FUNDING_MATERIALIZE_STATEMENT_TIMEOUT_MAX_MS = REVENUE_FUNDING_MATERIALIZE_STATEMENT_TIMEOUT_MS;
export const REVENUE_FUNDING_MATERIALIZE_WALL_MAX_MS = REVENUE_FUNDING_MATERIALIZE_WALL_MS;
export const REVENUE_FUNDING_MATERIALIZE_WALL_MIN_MS = 10_000;

export interface RevenueFundingMaterializeCronRequest {
  readonly lookbackDays?: number;
  /** Sealed days one call may materialize. Default {@link REVENUE_FUNDING_MATERIALIZE_MAX_DAYS_PER_CALL}. */
  readonly maxDays?: number;
  /** Per-day statement budget. Default {@link REVENUE_FUNDING_MATERIALIZE_STATEMENT_TIMEOUT_MS}. */
  readonly statementTimeoutMs?: number;
  /** Route wall budget. Default {@link REVENUE_FUNDING_MATERIALIZE_WALL_MS}. */
  readonly wallMs?: number;
}

export interface RevenueFundingMaterializeCronResponse {
  readonly ok: boolean;
  readonly materializedDays: number;
  readonly remaining_days: number;
  /** Booking days this call lost to statement_timeout (SQLSTATE 57014). */
  readonly timeout_days: number;
  readonly errors: readonly string[];
}
